import { Reveal } from "@/components/motion/Reveal";
import { PhoneCall, ServerCog, Rocket, CalendarCheck } from "lucide-react";

interface Step {
  n: string;
  title: string;
  owner: string;
  desc: string;
  icon: React.ComponentType<{ className?: string }>;
}

const DEFAULT_STEPS: Step[] = [
  {
    n: "01",
    title: "Strategy call",
    owner: "You + your Account Manager",
    desc: "We map your ICP, offer, and target volume. You approve the plan before anything ships.",
    icon: PhoneCall,
  },
  {
    n: "02",
    title: "We build & warm your infrastructure",
    owner: "Deliverability team",
    desc: "Dedicated domains, mailboxes, DNS, and 3–4 weeks of automated warm-up. You don't touch any of it.",
    icon: ServerCog,
  },
  {
    n: "03",
    title: "We launch & run outbound",
    owner: "Your AutoPitch team + AI",
    desc: "Prospecting, personalized emails, replies, follow-ups, and A/B on the messaging — every day, on your behalf.",
    icon: Rocket,
  },
  {
    n: "04",
    title: "Qualified meetings hit your calendar",
    owner: "You",
    desc: "You review the portal, join the meetings, close the deals. We only charge when a meeting is qualified.",
    icon: CalendarCheck,
  },
];

export function ServiceFlow({
  steps = DEFAULT_STEPS,
  eyebrow = "HOW THE SERVICE WORKS",
  headline = "You show up to meetings. We run everything else.",
  sub = "AutoPitch is a done-for-you outbound team. Every step below is handled by us — not you.",
}: {
  steps?: Step[];
  eyebrow?: string;
  headline?: string;
  sub?: string;
}) {
  return (
    <section className="relative">
      <div className="max-w-6xl mx-auto px-4 py-20 md:py-28">
        <Reveal stagger={60} className="text-center space-y-3 max-w-2xl mx-auto mb-12">
          <p className="text-[10px] font-mono uppercase tracking-widest text-primary">{eyebrow}</p>
          <h2 className="text-2xl md:text-4xl font-bold text-foreground tracking-tight">{headline}</h2>
          <p className="text-sm md:text-base text-foreground/80">{sub}</p>
        </Reveal>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
          {steps.map((s, i) => (
            <Reveal key={s.n} delay={i * 80}>
              <div className="relative h-full rounded-2xl border border-border/60 bg-card/50 backdrop-blur-xl p-6 space-y-4 hover:border-primary/40 transition-colors">
                <div className="flex items-center justify-between">
                  <span className="text-[10px] font-mono tracking-widest text-foreground/50">
                    STEP {s.n}
                  </span>
                  <div className="h-9 w-9 rounded-lg bg-primary/10 flex items-center justify-center">
                    <s.icon className="h-4 w-4 text-primary" />
                  </div>
                </div>
                <div className="space-y-1.5">
                  <h3 className="text-base font-semibold text-foreground leading-snug">{s.title}</h3>
                  <p className="text-[11px] font-mono uppercase tracking-widest text-primary/80">
                    {s.owner}
                  </p>
                </div>
                <p className="text-sm text-foreground/75 leading-relaxed">{s.desc}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}
